/**
 * Create the Accident Collect & Submit Agent node
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';
import { searchFAQs, formatSearchResults } from '../../utils/faq-search';

export function createAccidentCollectSubmitAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  // Track current phase: 'collect' or 'confirm'
  let currentPhase: 'collect' | 'confirm' = 'collect';
  // Track collected data internally
  let collectedAccidentType: string | null = null;
  let collectedInfo: { user_name?: string; car_model?: string; description?: string } = {};

  return {
    id: 'accident_collect_submit_agent',
    name: 'Accident Collect & Submit Agent',
    description: 'Combined: Classify type, collect info, confirm, submit, show result',
    
    // Initial prompt to trigger AI behavior

    
    // Backend provides the actual system instruction via ephemeral token

    
    // Initial prompt to trigger accident type classification
    initialPrompt: 'Ask the user to classify the accident type (minor accident, hit and run, or unknown party). Then collect their name, car model, and description. DO NOT re-announce the image analysis.',
    
    entryGuardrails: requireLanguage(),
    
    exitGuardrails: {
      transitions: new Map([
        ['new_report', {
          target: { agentId: 'accident_start_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['finish', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
        ['cancel', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: { agentId: 'router_agent' },
    },
    
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      if (toolName === 'set_accident_type') {
        collectedAccidentType = args.accident_type || 'minor_accident';
        console.log('[AccidentCollectSubmitAgent] Accident type set:', collectedAccidentType);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            accident_type: collectedAccidentType,
            message: `Accident type set to ${collectedAccidentType}. Now collect user info.`,
          },
        };
      }
      
      if (toolName === 'collect_accident_info') {
        collectedInfo = {
          user_name: args.user_name,
          car_model: args.car_model,
          description: args.description,
        };
        console.log('[AccidentCollectSubmitAgent] Info collected:', collectedInfo);
        
        // Get existing accident data from context
        const existingData = context.transitionData.accidentData || {};
        
        // Combine all collected data
        const completeAccidentData = {
          ...existingData,
          accident_type: collectedAccidentType,
          ...collectedInfo,
        };
        
        return {
          toolName,
          args,
          result: {
            success: true,
            collected_info: collectedInfo,
            accident_data: completeAccidentData,
            message: 'All information collected. Call show_confirmation to display confirmation screen.',
          },
          // Update transition data even though we're not transitioning
          transitionData: {
            accidentData: completeAccidentData,
          },
        };
      }
      
      if (toolName === 'show_confirmation') {
        console.log('[AccidentCollectSubmitAgent] Showing confirmation screen');
        currentPhase = 'confirm';
        
        return {
          toolName,
          args,
          result: {
            success: true,
            message: 'Confirmation screen is now visible. Present data summary and wait for user to confirm.',
          },
        };
      }
      
      if (toolName === 'confirm_submission') {
        const confirmed = args.confirmed;
        console.log('[AccidentCollectSubmitAgent] User confirmed:', confirmed);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            message: confirmed ? 'User confirmed, waiting for submission' : 'User wants to modify',
          },
        };
      }
      
      if (toolName === 'submission_complete') {
        const success = args.success;
        const reportNumber = args.report_number;
        const errorMessage = args.error_message;
        
        console.log('[AccidentCollectSubmitAgent] Submission complete:', { success, reportNumber, errorMessage });
        
        return {
          toolName,
          args,
          result: {
            success,
            report_number: reportNumber,
            error_message: errorMessage,
            message: success ? 'Report submitted successfully' : 'Submission failed',
          },
        };
      }
      
      if (toolName === 'new_report') {
        console.log('[AccidentCollectSubmitAgent] Starting new report');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting new accident report' },
          triggersTransition: true,
          transitionTo: 'accident_start_agent',
          transitionData: {
            accidentData: undefined, // Clear previous data
          },
        };
      }
      
      if (toolName === 'cancel_accident') {
        console.log('[AccidentCollectSubmitAgent] User cancelled');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Cancelled' },
          triggersTransition: true,
          transitionTo: 'router_agent',
          transitionData: {
            accidentData: undefined,
          },
        };
      }
      
      // Cross-flow navigation - Renewal
      if (toolName === 'start_renewal') {
        const reportNumber = args.report_number;
        console.log('[AccidentCollectSubmitAgent] Cross-flow: Starting renewal', reportNumber ? `for report ${reportNumber}` : '');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting renewal process' },
          triggersTransition: true,
          transitionTo: 'renewal_input_agent',
          transitionData: {
            accidentData: undefined,
            prefilledReportNumber: reportNumber,
          },
        };
      }
      
      // Cross-flow navigation - Lost report search
      if (toolName === 'start_lost_report_search') {
        console.log('[AccidentCollectSubmitAgent] Cross-flow: Starting lost report search');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Starting lost report search' },
          triggersTransition: true,
          transitionTo: 'lost_report_upload_extract_agent',
          transitionData: {
            accidentData: undefined,
          },
        };
      }
      
      // Cross-flow navigation - FAQ
      if (toolName === 'go_to_faq') {
        console.log('[AccidentCollectSubmitAgent] Cross-flow: Going to FAQ');
        
        return {
          toolName,
          args,
          result: { success: true, message: 'Going to FAQ' },
          triggersTransition: true,
          transitionTo: 'faq_agent',
          transitionData: {
            accidentData: undefined,
          },
        };
      }
      
      // Inline FAQ
      if (toolName === 'search_faq') {
        const question = args.question || '';
        const searchResults = await searchFAQs(question, 3, 2);
        const language = context.transitionData.language || 'en';
        const formattedResults = formatSearchResults(searchResults, language);
        
        console.log('[AccidentCollectSubmitAgent] Inline FAQ:', question);
        
        return {
          toolName,
          args,
          result: {
            success: true,
            results_count: searchResults.length,
            results: formattedResults,
            message: currentPhase === 'collect' 
              ? 'Answer the question, then continue collecting information.'
              : 'Answer the question, then remind user of their options.',
          },
          triggersTransition: false,
        };
      }
      
      return {
        toolName,
        args,
        result: { success: false, error: 'Unknown tool' },
      };
    },
    
    onActivate: async (context: AgentContext) => {
      currentPhase = 'collect';
      collectedAccidentType = null;
      collectedInfo = {};
      console.log('[AccidentCollectSubmitAgent] Activated - Combined collect & submit flow');
      console.log('  Previous analysis:', context.transitionData.accidentData?.imageAnalysis);
    },
    
    onDeactivate: async (context: AgentContext) => {
      console.log('[AccidentCollectSubmitAgent] Deactivated');
    },
  };
}

export default createAccidentCollectSubmitAgent;

